/**
 * Turning a stored `Business` row into form fields, and back.
 *
 * WHY THIS IS NOT INSIDE THE SCREEN
 * ---------------------------------
 * There is no React testing library in this project, so the only way to test
 * logic is to keep it out of the component. This is the part of the profile
 * screen that can actually be wrong — a GSTIN that comes back lower-cased, a
 * state that is silently reset to a default, an address whose lines are lost —
 * and all of it is here rather than in JSX.
 *
 * ADDRESS IS ONE TEXTAREA, NOT AN ARRAY WIDGET
 * --------------------------------------------
 * A letterhead is read top to bottom, so "one line per row" is how people
 * already keep an address written down. Splitting it into a repeating set of
 * single-line inputs to fit a data model is the model dictating the UI, and it
 * produces a form where the common case — a two-line address — takes two
 * clicks to enter correctly. Lines are split on save, because that is the
 * storage shape, not the shape a person types in.
 */
import { GST_STATES } from '@core/gst/stateCodes';
import type { BusinessProfileInput } from '@data/services/businessService';
import type { Business, DocumentSettings } from '@data/db/types';

/**
 * The longest default-terms text accepted, matching `documentSchema`'s
 * `terms` maximum.
 *
 * Duplicated here as a named constant rather than read from the schema on every
 * keystroke: the schema is a frozen sync contract that this milestone is not
 * touching, and a UI limit that could shift when sync is un-frozen would be
 * worse than a documented one that does not. The `businessProfileForm` test
 * asserts the two agree, so a later divergence is caught rather than shipped.
 */
export const TERMS_MAX_LENGTH = 4000;

/** Every field the screen edits, as strings, because that is what inputs hold. */
export interface ProfileFormState {
  legalName: string;
  tradeName: string;
  gstin: string;
  pan: string;
  stateCode: string;
  /** Newline-separated. Split into `addressLines` on save. */
  addressText: string;
  city: string;
  district: string;
  pincode: string;
  phone: string;
  email: string;
  website: string;
  upiId: string;
  /** A string, because `<select>` values are strings even for a month number. */
  fyStartMonth: string;
  terms: string;
  roundOffEnabled: boolean;
  showBankDetails: boolean;
  /**
   * Attachment ids, or '' when there is none.
   *
   * A string because that is what an `<input type="file">` hands back and what
   * a "remove" button can set. NOT persisted as part of the profile text: these
   * are pointers to rows in `attachments`, and the form only carries which rows
   * the business should point at.
   */
  logoAttachmentId: string;
  signatureAttachmentId: string;
}

/** The GST states, as select options. Built from the one canonical table. */
export const STATE_OPTIONS = GST_STATES.map((s) => ({ value: s.code, label: s.name }));

/**
 * A blank form.
 *
 * `stateCode` starts EMPTY, not on a default. A profile that opens already
 * showing "Telangana" is how 1.6.x came to issue CGST/SGST where IGST was owed:
 * the wrong state was never chosen, it was merely never asked for.
 */
export function emptyProfileForm(): ProfileFormState {
  return {
    legalName: '',
    tradeName: '',
    gstin: '',
    pan: '',
    stateCode: '',
    addressText: '',
    city: '',
    district: '',
    pincode: '',
    phone: '',
    email: '',
    website: '',
    upiId: '',
    fyStartMonth: '4',
    terms: '',
    roundOffEnabled: true,
    showBankDetails: true,
    logoAttachmentId: '',
    signatureAttachmentId: '',
  };
}

/** A form showing an existing business, unchanged. */
export function profileFormFromBusiness(business: Business): ProfileFormState {
  return {
    legalName: business.legalName ?? '',
    tradeName: business.tradeName ?? '',
    gstin: business.gstin ?? '',
    pan: business.pan ?? '',
    stateCode: business.stateCode ?? '',
    addressText: (business.addressLines ?? []).join('\n'),
    city: business.city ?? '',
    district: business.district ?? '',
    pincode: business.pincode ?? '',
    phone: business.phone ?? '',
    email: business.email ?? '',
    website: business.website ?? '',
    upiId: business.upiId ?? '',
    fyStartMonth: String(business.fyStartMonth ?? 4),
    terms: business.documentSettings?.terms ?? '',
    roundOffEnabled: business.documentSettings?.roundOffEnabled ?? true,
    showBankDetails: business.documentSettings?.showBankDetails ?? true,
    logoAttachmentId: business.logoAttachmentId ?? '',
    signatureAttachmentId: business.signatureAttachmentId ?? '',
  };
}

/** The service input, plus the document settings this screen owns. */
export interface ProfileSubmission extends BusinessProfileInput {
  readonly documentSettings: Partial<DocumentSettings>;
}

/** The form as the service wants it. Trimmed; validation is the service's job. */
export function profileInputFromForm(form: ProfileFormState): ProfileSubmission {
  return {
    legalName: form.legalName,
    tradeName: form.tradeName,
    gstin: form.gstin,
    pan: form.pan,
    stateCode: form.stateCode,
    addressLines: form.addressText.split('\n'),
    city: form.city,
    district: form.district,
    pincode: form.pincode,
    phone: form.phone,
    email: form.email,
    website: form.website,
    upiId: form.upiId,
    // ALWAYS sent, including when empty - unlike every optional text field
    // above. Those are typed values where blank means "leave whatever is there",
    // because blanking a live letterhead by re-saving an unrelated field is a
    // terrible surprise. This is a POINTER the form authoritatively owns: '' is
    // a decision, "print no logo", and it has to be expressible.
    logoAttachmentId: form.logoAttachmentId,
    signatureAttachmentId: form.signatureAttachmentId,
    fyStartMonth: Number(form.fyStartMonth),
    documentSettings: {
      terms: form.terms,
      roundOffEnabled: form.roundOffEnabled,
      showBankDetails: form.showBankDetails,
    },
  };
}
